import React, { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect } from "react";
import {
  Menu,
  X,
  Instagram,
  Facebook,
  Youtube,
  MessageCircle,
  Music,
  UserCircle2,
  LogIn,
} from "lucide-react";
import SoccerBall from "@/components/SoccerBall";
import NewsletterSignup from "@/components/NewsletterSignup";
import { useSiteSettings, socialLinksFrom } from "@/hooks/useSiteSettings";
import { useCollectorAuth } from "@/context/CollectorAuthContext";
import { PLATFORM_NAME } from "@/config/brand";

const links = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/shop", label: "Shop", testid: "nav-shop" },
  { to: "/order-missing", label: "Missing Stickers", testid: "nav-missing" },
  { to: "/events", label: "Events", testid: "nav-events" },
  { to: "/certificates", label: "Certificates", testid: "nav-registry" },
  { to: "/about", label: "About", testid: "nav-about" },
  { to: "/contact", label: "Contact", testid: "nav-contact" },
];

const socialIcons = {
  instagram: Instagram,
  tiktok: Music,
  facebook: Facebook,
  youtube: Youtube,
  whatsapp: MessageCircle,
};

const footerSections = [
  {
    heading: "Shop & Services",
    items: [
      { to: "/shop", label: "Store" },
      { to: "/order-missing", label: "Order Missing Stickers" },
      { to: "/events", label: "Events" },
      { to: "/certificates", label: "Certificates" },
    ],
  },
  {
    heading: "Company",
    items: [
      { to: "/about", label: "About Gil" },
      { to: "/contact", label: "Contact" },
    ],
  },
  {
    heading: "Collectors",
    items: [
      { to: "/register", label: "Create Account" },
      { to: "/login", label: "Sign In" },
      { to: "/certificates", label: "Certificates" },
    ],
  },
  {
    heading: "Policies",
    items: [
      { to: "/privacy-policy", label: "Privacy Policy" },
      { to: "/terms-of-service", label: "Terms of Service" },
      { to: "/shipping-policy", label: "Shipping Policy" },
      { to: "/return-policy", label: "Return Policy" },
    ],
  },
];

/** Iter QA — canonical Collector Up brand logo asset.
 *  The logo image contains the full brandmark (album icon + arrow +
 *  "CollectorUp.com" + "By Gil Maakaroun") set inside a locked black
 *  rounded-rectangle with a gold border. We NEVER pair it with additional
 *  wordmark text, and we NEVER wrap it in any card/box/border that would
 *  double the visible frame. `object-contain` guarantees the aspect ratio
 *  and the full gold border stay visible on every screen size.
 *
 *  Height budget (per brand spec):
 *    mobile   : 40px  (spec 36–44)
 *    tablet+  : 52px  (spec 50–60)
 *    desktop+ : 60px  (spec 50–60)
 *  Widths are proportional (aspect ≈ 4.25 : 1) — never forced.
 */
function BrandMark({ settings }) {
  const src = settings.logo_url || "/collector-up-logo.webp";
  return (
    <img
      src={src}
      alt="CollectorUp.com · By Gil Maakaroun"
      loading="eager"
      decoding="async"
      className="block h-10 md:h-[52px] lg:h-[60px] w-auto object-contain select-none"
      style={{ maxWidth: "min(72vw, 340px)" }}
    />
  );
}

export default function Layout() {
  const settings = useSiteSettings();
  const { collector, loading: collectorLoading } = useCollectorAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const socials = socialLinksFrom(settings);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0 });
  }, [loc.pathname]);

  // Live favicon swap from Website Settings, if the owner supplied one.
  useEffect(() => {
    const href = (settings.favicon_url || "").trim();
    if (!href) return;
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = href;
  }, [settings.favicon_url]);

  // Header-Stack Measurement (Certificate Order Production Defect 1)
  //
  // The public Layout renders THREE stacked elements above page content:
  //   1. Announcement bar (only when configured; non-sticky)
  //   2. .pitch-band divider (non-sticky)
  //   3. <header sticky top-0 z-50>
  //
  // Before this fix, downstream fixed/absolute drawers offset themselves
  // by the header height alone (`top-20 md:top-24`). At scroll=0 the
  // announcement + pitch-band still occupy the top of the viewport, so
  // the drawer opens overlapping the announcement / header stack.
  //
  // We expose the CURRENT visible top-stack height as a CSS variable
  // (`--app-header-height`) on `document.documentElement`, updated on
  // resize + scroll + settings change. Any component (drawers, modals,
  // sticky sub-nav) can consume it via:
  //     top: var(--app-header-height, 96px)
  //     height: calc(100vh - var(--app-header-height, 96px))
  useEffect(() => {
    const measure = () => {
      const header = document.querySelector("[data-testid='site-header']");
      if (!header) return;
      const rect = header.getBoundingClientRect();
      // rect.bottom is the CURRENT distance from viewport-top to the
      // bottom of the sticky header — pre-scroll this includes the
      // announcement + pitch-band; post-scroll it's just the header.
      // We clamp to header-only height as a floor so the drawer never
      // travels UP past the sticky header once scrolled.
      const value = Math.max(rect.height, rect.bottom);
      document.documentElement.style.setProperty(
        "--app-header-height", `${Math.round(value)}px`);
    };
    measure();
    window.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      const h = document.querySelector("[data-testid='site-header']");
      if (h) ro.observe(h);
    }
    return () => {
      window.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
      if (ro) ro.disconnect();
    };
  }, [settings.announcement_bar]);

  const businessName = settings.business_name || "Gil Maakaroun";
  const year = new Date().getFullYear();
  const copyright =
    settings.footer_copyright && settings.footer_copyright.trim()
      ? settings.footer_copyright
      : `${PLATFORM_NAME}. Created by ${businessName}. Made for collectors.`;

  return (
    <div className="min-h-screen bg-[var(--cream)] text-[var(--ink)]">
      {/* Announcement bar — only rendered when configured */}
      {settings.announcement_bar && settings.announcement_bar.trim() && (
        <div
          className="bg-[var(--panini-red)] text-white text-sm py-2 px-4 text-center border-b-2 border-[var(--ink)]"
          data-testid="announcement-bar"
        >
          {settings.announcement_bar}
        </div>
      )}

      <div className="pitch-band" aria-hidden />

      {/* Nav */}
      <header
        className="bg-white border-b-2 border-[var(--ink)] sticky top-0 z-50"
        data-testid="site-header"
      >
        <div className="container-gm flex items-center justify-between h-20 md:h-24">
          <Link to="/" className="flex items-center shrink-0" data-testid="brand-link" aria-label="Collector Up — Home">
            <BrandMark settings={settings} />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-full text-[13px] font-bold whitespace-nowrap transition-all ${
                    isActive
                      ? "bg-[var(--ink)] text-[var(--panini-yellow)]"
                      : "text-[var(--ink)] hover:bg-[var(--panini-yellow-soft)]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            {!collectorLoading && (
              collector ? (
                <Link
                  to="/dashboard"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-full border-2 border-[var(--ink)] text-[var(--ink)] hover:bg-[var(--panini-yellow-soft)] text-sm font-bold"
                  data-testid="nav-dashboard"
                  title={collector.display_name}
                >
                  <UserCircle2 size={16} />
                  <span className="max-w-[8rem] truncate">
                    {collector.display_name || "My Account"}
                  </span>
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[var(--ink)] hover:bg-[var(--panini-yellow-soft)] text-sm font-bold whitespace-nowrap"
                    data-testid="nav-login"
                  >
                    <LogIn size={14} /> Sign in
                  </Link>
                  <Link
                    to="/register"
                    className="inline-flex items-center px-3 py-2 rounded-full bg-[var(--panini-yellow)] border-2 border-[var(--ink)] text-[var(--ink)] text-sm font-bold hover:bg-[var(--panini-yellow-soft)] whitespace-nowrap"
                    data-testid="nav-register"
                  >
                    Create account
                  </Link>
                </>
              )
            )}
            <Link
              to="/order-missing"
              className="btn-red py-2 px-4 text-sm whitespace-nowrap"
              data-testid="nav-cta"
            >
              Order Stickers
            </Link>
          </div>

          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 -mr-2 text-[var(--ink)]"
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden bg-white border-t-2 border-[var(--ink)]" data-testid="mobile-menu">
            <div className="container-gm py-4 flex flex-col gap-1">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`${l.testid}-mobile`}
                  className={({ isActive }) =>
                    `py-2.5 px-3 rounded-lg font-bold text-base ${
                      isActive
                        ? "bg-[var(--panini-yellow)] text-[var(--ink)]"
                        : "text-[var(--ink)]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}

              <div className="mt-3 pt-3 border-t-2 border-[var(--line)] flex flex-col gap-2" data-testid="mobile-auth-block">
                {!collectorLoading && (
                  collector ? (
                    <Link
                      to="/dashboard"
                      className="py-2.5 px-3 rounded-lg bg-[var(--ink)] text-[var(--panini-yellow)] font-bold text-base inline-flex items-center gap-2"
                      data-testid="nav-dashboard-mobile"
                    >
                      <UserCircle2 size={16} />
                      My Collector Account
                    </Link>
                  ) : (
                    <>
                      <Link
                        to="/login"
                        className="py-2.5 px-3 rounded-lg border-2 border-[var(--ink)] text-[var(--ink)] font-bold text-base inline-flex items-center gap-2"
                        data-testid="nav-login-mobile"
                      >
                        <LogIn size={14} /> Sign in
                      </Link>
                      <Link
                        to="/register"
                        className="py-2.5 px-3 rounded-lg bg-[var(--panini-yellow)] border-2 border-[var(--ink)] text-[var(--ink)] font-bold text-base"
                        data-testid="nav-register-mobile"
                      >
                        Create Collector account
                      </Link>
                    </>
                  )
                )}
                <Link
                  to="/order-missing"
                  className="btn-red mt-1 justify-center"
                  data-testid="nav-cta-mobile"
                >
                  Order Missing Stickers
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      <main className="min-h-[70vh]" data-testid="page-main">
        <Outlet />
      </main>

      {/* Footer — hidden on admin sales routes so operators get a
          clean, POS-style workspace with no marketing scroll. */}
      {!(loc.pathname === "/admin/new-sale"
        || loc.pathname === "/admin/quick-sale"
        || loc.pathname.startsWith("/admin/new-sale/")
        || loc.pathname.startsWith("/admin/quick-sale/")) && (
      <footer className="mt-20" data-testid="site-footer">
        <div className="pitch-band" aria-hidden />
        <div className="bg-[var(--ink)] text-white">
          {/* Newsletter band */}
          <div className="container-gm py-12 md:py-14 border-b border-white/10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="kicker kicker-yellow">The mailing list</div>
                <h3 className="font-display text-3xl md:text-4xl mt-2">
                  Get first look at drops and events.
                </h3>
                <p className="text-white/70 mt-3 max-w-md text-sm leading-relaxed">
                  {settings.newsletter_text}
                </p>
              </div>
              <div>
                <NewsletterSignup variant="dark" />
              </div>
            </div>
          </div>

          {/* Main footer grid */}
          <div className="container-gm py-14 grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <div className="flex items-center gap-3">
                <BrandMark settings={settings} size={44} />
              </div>
              <p className="text-white/70 mt-5 text-sm leading-relaxed max-w-xs">
                Albums, packs, boxes, accessories, Gold Crumples, and the
                numbered Certificate of Completion program. Nationwide
                shipping.
              </p>

              {/* Contact block — only shows lines that are set */}
              <ul className="mt-6 space-y-1.5 text-sm text-white/75" data-testid="footer-contact">
                {settings.contact_email && (
                  <li>
                    <a
                      href={`mailto:${settings.contact_email}`}
                      className="hover:text-[var(--panini-yellow)]"
                      data-testid="footer-email"
                    >
                      {settings.contact_email}
                    </a>
                  </li>
                )}
                {settings.contact_phone && (
                  <li>
                    <a
                      href={`tel:${settings.contact_phone.replace(/\s+/g, "")}`}
                      className="hover:text-[var(--panini-yellow)]"
                      data-testid="footer-phone"
                    >
                      {settings.contact_phone}
                    </a>
                  </li>
                )}
                {settings.business_address && (
                  <li className="text-white/60" data-testid="footer-address">
                    {settings.business_address}
                  </li>
                )}
              </ul>

              {socials.length > 0 && (
                <div className="mt-6 flex items-center gap-3" data-testid="footer-socials">
                  {socials.map((s) => {
                    const Icon = socialIcons[s.key] || Instagram;
                    return (
                      <a
                        key={s.key}
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={s.label}
                        data-testid={`social-${s.key}`}
                        className="w-10 h-10 rounded-full border-2 border-white/25 hover:border-[var(--panini-yellow)] flex items-center justify-center text-white/80 hover:text-[var(--panini-yellow)] transition-colors"
                      >
                        <Icon size={16} />
                      </a>
                    );
                  })}
                </div>
              )}
            </div>

            {footerSections.map((section) => (
              <div key={section.heading} className="md:col-span-2">
                <div className="kicker kicker-yellow">{section.heading}</div>
                <ul className="mt-4 space-y-2.5 text-sm">
                  {section.items.map((item) => (
                    <li key={item.to}>
                      <Link
                        to={item.to}
                        className="text-white/85 hover:text-[var(--panini-yellow)] font-semibold"
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            <div className="md:col-span-2">
              <div className="kicker kicker-yellow">The Fine Print</div>
              <p
                className="mt-4 text-xs leading-relaxed text-white/70"
                data-testid="footer-disclaimer"
              >
                {settings.disclaimer_footer}
              </p>
            </div>
          </div>

          <div className="border-t border-white/10">
            <div className="container-gm py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs">
              <div className="text-white/50" data-testid="footer-copyright">
                © {year} {copyright}
              </div>
              <div className="flex items-center gap-5">
                <Link
                  to="/privacy-policy"
                  className="text-white/50 hover:text-[var(--panini-yellow)]"
                >
                  Privacy
                </Link>
                <Link
                  to="/terms-of-service"
                  className="text-white/50 hover:text-[var(--panini-yellow)]"
                >
                  Terms
                </Link>
                <Link
                  to="/admin/login"
                  className="text-white/50 hover:text-[var(--panini-yellow)]"
                  data-testid="footer-admin-link"
                >
                  Admin
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
      )}
    </div>
  );
}
